import { useMemo } from 'react'
import { useHistoricalAction } from 'hooks/FeatureFlag/useHistoricalAction'

/**
 * Hook to get active amenity filter values from historical actions.
 *
 * Returns a Set of amenity filter values that are currently active
 * (where the latest action has active: true).
 *
 * This is used to boost matching amenities to the top of lists when
 * users have active amenity filters selected.
 *
 * @returns Set of active amenity filter values (e.g., Set(['Private pool', 'Coffee machine']))
 */
export function useActiveAmenityFilters(): Set<string> {
  // Get all amenity filter values the user has selected in this session
  // we're not using onlyLatest here because we want to get the latest action for each amenity value
  // that's in historicalAction. 'onlyLatest = true' in this case would only return the latest 'filter_selected' action
  // matching filterType == 'amenities'
  const { allMatches: amenityFilterActions } = useHistoricalAction('filter_selected', {
    metadataMatcher: (metadata) => metadata?.filterType === 'amenities' && metadata?.value,
  })

  // Extract filter values from historical actions into a stable Set
  // Only include amenities that are currently active (latest action has active: true)
  return useMemo(() => {
    if (!amenityFilterActions.length) return new Set<string>()

    // Group by amenity value and get the latest action for each
    const latestByValue = new Map<string, App.HistoricalAction>()
    amenityFilterActions.forEach(action => {
      const value = action.metadata?.value as string
      if (value) {
        const existing = latestByValue.get(value)
        if (!existing || new Date(action.timestamp) > new Date(existing.timestamp)) {
          latestByValue.set(value, action)
        }
      }
    })

    // Only include values where the latest action has active: true (or undefined for backwards compatibility)
    const activeValues = Array.from(latestByValue.values())
      .filter(action => action.metadata?.active === true)
      .map(action => action.metadata?.value)

    return new Set(activeValues)
  }, [amenityFilterActions])
}
